import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Table } from '@getsynapse/design-system';
import { CapacityManagementDrillDownItem } from 'types/store/normalized/insightsDrillDown';
import { SortingType } from 'utils/customTypes';
import { orderBy } from 'lodash';
import { CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS } from 'utils/constants/dashboard';
import CapacityManagementDrillDownTableHeader from './CapacityManagementDrillDownTableHeader/CapacityManagementDrillDownTableHeader';
import CapacityManagementDrillDownTableBody from './CapacityManagementDrillDownTableBody/CapacityManagementDrillDownTableBody';
import { useSelector } from 'react-redux';
import drilldownSelectors from 'state/normalized/insights/drilldownSelectors';
import DrillTableHeaderFilters from 'Pages/DashboardsDetailsPage/components/DrillTableHeaderFilters';
import { handleCapacityManagementExportData } from 'Pages/DashboardsDetailsPage/components/DrillDownSidePanel/drillDownDataExportHandlers';
import { insightDrilldownMetadataSelectors } from 'state/normalized/insightDrilldownMetadata/insightDrilldownMetadataSlice';
import TableSkeletonLoader from 'Organisms/TableSkeletonLoader/TableSkeletonLoader';

const columnMap = new Map<string, string>([
  [CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.NAME, 'user_name'],
  [CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_TYPE, 'user_type'],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_NUMBER,
    'task_number',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_TITLE,
    'task_name',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_STATUS,
    'task_status',
  ],
  [CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.TASK_TYPE, 'task_type'],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ALLOCATED_HOURS,
    'allocated_hours_in_range',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.ACTUAL_HOURS,
    'actual_hours_in_range',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_TITLE,
    'project_title',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.BUSINESS_UNITS,
    'business_units[0].title',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROGRAMS,
    'programs[0].title',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.PROJECT_TEAMS,
    'project_learning_teams[0].name',
  ],
  [
    CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS.USER_TEAMS,
    'user_learning_teams[0].name',
  ],
]);

const CapacityManagementDrillDownTable = () => {
  const items = useSelector(
    drilldownSelectors.selectActiveDrilldownCapacityManagement
  );
  const cardName = useSelector(
    drilldownSelectors.selectActiveDrilldownCardName
  );
  const isLoading = useSelector(
    insightDrilldownMetadataSelectors.selectIsActiveDrilldownLoading
  );

  const exportItems = () => {
    handleCapacityManagementExportData(items);
  };
  const [orderByParam, setOrderByParam] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const orderByCapacityManagementTableData = (
    items: CapacityManagementDrillDownItem[],
    column: string,
    order: SortingType
  ) => {
    if (!items || items.length === 0) return [];

    const fieldName = columnMap.get(column) || '';
    const finalData = orderBy(items, [fieldName], [order]);
    return finalData;
  };

  const sortedItems = useMemo(() => {
    return orderByCapacityManagementTableData(items, orderByParam, order);
  }, [items, orderByParam, order]);

  const handleSort = useCallback(
    (column: string) => {
      const isSameColumn = column === orderByParam;
      const newOrder = isSameColumn && order === 'asc' ? 'desc' : 'asc';
      setOrderByParam(column);
      setOrder(newOrder);
    },
    [orderByParam, order]
  );

  return (
    <div>
      <DrillTableHeaderFilters
        title={cardName}
        onExportClick={exportItems}
        exportEnabled
      />
      <div
        className={classnames(
          'w-full rounded-b overflow-auto border border-neutral-lighter-two max-h-filtered-table-body'
        )}
      >
        {isLoading && <TableSkeletonLoader />}
        {!isLoading && (
          <Table
            className={classnames('relative', {
              'max-w-full border-0 w-full': items.length,
              'w-full': !items.length,
            })}
            canSelectRows={false}
            data={{
              headData: {
                stickyHeader: true,
                handleSort,
                headCells: [],
              },
              rows: [],
              total: items.length,
            }}
            data-cy='team-dashboards-table'
          >
            <CapacityManagementDrillDownTableHeader handleSort={handleSort} />
            <CapacityManagementDrillDownTableBody items={sortedItems} />
          </Table>
        )}
      </div>
    </div>
  );
};

export default CapacityManagementDrillDownTable;
